/**
 * Turn a file card's stored markdown into a layout an attorney can read.
 * The markdown string is the source of truth and is never rewritten: this
 * only describes how to show it. A line that does not match a known shape
 * is returned as plain text so nothing is dropped.
 */

export const cardTabTitle = (fileTitle: string) => `Card: ${fileTitle}`;

export type FileCardRow = {
  /** The stored field kind ("party"). Null for quotes, concerns and plain lines. */
  kind: string | null;
  /** Display label for the kind ("Party"). */
  kindLabel: string | null;
  /** "Profile Subject". Null when the line has no label. */
  label: string | null;
  /** The fact, quote or leftover line, with every stored word still present. */
  value: string;
  /** A trailing parenthetical ("Beneficiary; subject of the profile."). */
  detail: string | null;
  /** "p. 1". Null when the line names no page. */
  page: string | null;
  /** What the line says it supports, verbatim, one entry per criterion. */
  supports: string[];
  key: boolean;
};

export type FileCardGroup = {
  /** The criteria this group supports. Empty for fields tied to none. */
  supports: string[];
  rows: FileCardRow[];
};

export type FileCardGlance = { label: string; value: string };

export type FileCardSection = {
  title: string;
  note: string | null;
  rows: FileCardRow[];
};

export type FileCardSummary = {
  title: string | null;
  /** Document type, author, date, signed, language, pages: short labeled pairs. */
  glance: FileCardGlance[];
  /** Header bits that matched no known slot, shown as written. */
  about: string[];
  /** Number of pages, from the card's meta line. */
  pageCount: number | null;
  summary: string | null;
  /** The attorney's corrections the card was rebuilt with ("Jane Doe · 2026-11-02"). */
  corrections: { by: string | null; text: string }[];
  concerns: FileCardSection | null;
  /** Fields grouped by what they support, in the card's order. */
  fieldGroups: FileCardGroup[];
  fieldsNote: string | null;
  quotes: FileCardSection | null;
  /** Any other section the card carries. */
  sections: FileCardSection[];
  /** "Page 4: the signature is handwritten". */
  lookAt: { page: string | null; text: string }[];
  /** "Pages 1-2 of 4". */
  pagesRead: string | null;
  notes: string[];
  /** File reference and source path. */
  footnotes: string[];
};

const KIND_LABEL: Record<string, string> = {
  party: "Party",
  fact: "Fact",
  amount: "Amount",
  period: "Period",
  significance: "Significance",
  concern: "Concern",
  date: "Date",
  role: "Role",
  name: "Name"
};

const FIELD = /^- (F\d+)(?: (p\d+))? (\w+) · (.*)$/;
const QUOTE = /^- (Q\d+)(?: (p\d+))?( key)?: (.*)$/;
const CONCERN = /^- (C\d+)(?: (p\d+))?: (.*)$/;
const CORRECTION = /^Attorney correction(?: \(([^)]*)\))?: (.+)$/;
const META = /^`([^`]*)`(?: · `([^`]*)`)?(?: · (.+))?$/;
const DATE_LIKE =
  /^(\d{1,4}[/.-]\d{1,2}([/.-]\d{1,4})?|(\d{1,2} )?(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\w*\.?( \d{1,2},?)?( \d{4})?|\d{4})$/i;

const SMALL_WORDS = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "by",
  "for",
  "in",
  "of",
  "on",
  "or",
  "the",
  "to",
  "with"
]);

/** "profile subject" → "Profile Subject"; words already capitalized stay. */
export const titleCase = (text: string) =>
  text
    .split(" ")
    .map((word, i) =>
      i > 0 && SMALL_WORDS.has(word.toLowerCase())
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(" ");

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

/** "5.1." → "5.1"; "Exhibit A" → "A". */
export const exhibitName = (label?: string | null) =>
  label
    ?.trim()
    .replace(/\.+$/, "")
    .replace(/^exhibit\s+/i, "")
    .trim() || null;

const pageOf = (token: string | undefined) =>
  token ? `p. ${token.replace(/^p/, "")}` : null;

const kindLabel = (kind: string) =>
  KIND_LABEL[kind] ?? (capitalize(kind) || null);

/** The trailing " → tags" is support; an arrow inside parentheses is part of the fact. */
const splitSupports = (body: string): { text: string; supports: string[] } => {
  const at = body.lastIndexOf(" → ");
  if (at < 0) return { text: body.trim(), supports: [] };
  const tail = body.slice(at + 3).trim();
  if (!tail || /[()]/.test(tail)) return { text: body.trim(), supports: [] };
  return {
    text: body.slice(0, at).trim(),
    supports: tail
      .split(", ")
      .map((tag) => tag.trim())
      .filter(Boolean)
  };
};

const readable = (text: string) =>
  text.replace(/ · /g, ". ").replace(/\.\. /g, ". ").trim();

/** "Khalil Zlaoui (Beneficiary; subject of the profile.)" → value and detail. */
const splitDetail = (
  text: string
): { value: string; detail: string | null } => {
  if (!text.endsWith(")")) return { value: text, detail: null };
  let depth = 0;
  for (let i = text.length - 1; i >= 0; i -= 1) {
    if (text[i] === ")") depth += 1;
    else if (text[i] === "(") {
      depth -= 1;
      if (depth === 0) {
        const value = text.slice(0, i).trim();
        if (!value) return { value: text, detail: null };
        return { value, detail: capitalize(text.slice(i + 1, -1).trim()) };
      }
    }
  }
  return { value: text, detail: null };
};

const plain = (line: string) =>
  line.replace(/^- /, "").replace(/`/g, "").trim();

const row = (
  value: string,
  extras: Partial<FileCardRow> = {}
): FileCardRow => ({
  kind: null,
  kindLabel: null,
  label: null,
  value,
  detail: null,
  page: null,
  supports: [],
  key: false,
  ...extras
});

const fieldRow = (
  kind: string,
  pageToken: string | undefined,
  body: string
) => {
  const { text, supports } = splitSupports(body);
  let rest = text;
  let group: string | null = null;
  const dot = rest.indexOf(" · ");
  const colon = rest.indexOf(": ");
  if (dot >= 0 && (colon < 0 || dot < colon)) {
    group = rest.slice(0, dot).trim();
    rest = rest.slice(dot + 3).trim();
  }
  const split = rest.indexOf(": ");
  const rawLabel = split > 0 ? rest.slice(0, split).trim() : null;
  const { value, detail } = splitDetail(
    readable(split > 0 ? rest.slice(split + 2) : rest)
  );
  const label = [group, rawLabel].filter(Boolean).map((bit) => titleCase(bit!));
  return row(value, {
    kind,
    kindLabel: kindLabel(kind),
    label: label.length ? label.join(" · ") : null,
    detail,
    page: pageOf(pageToken),
    supports
  });
};

/** Slots in the order the card writes its header line. */
const SLOTS = [
  "Document Type",
  "Filed Under",
  "Author",
  "Date",
  "Recipient",
  "Signed",
  "Beneficiary",
  "Language"
] as const;

type Slot = (typeof SLOTS)[number];

/** Which slot a header bit fills, given how far along the line it sits. */
const slotOf = (bit: string, cursor: number): [Slot, string] | null => {
  const date = SLOTS.indexOf("Date");
  if (bit.startsWith("by ")) return ["Author", bit.slice(3)];
  if (bit.startsWith("to ")) return ["Recipient", bit.slice(3)];
  if (bit === "signed") return ["Signed", "Yes"];
  if (bit === "not signed") return ["Signed", "No"];
  if (bit.startsWith("beneficiary: "))
    return ["Beneficiary", capitalize(bit.slice(13))];
  if (DATE_LIKE.test(bit) && cursor <= date) return ["Date", bit];
  if (cursor === 0) return ["Document Type", capitalize(bit)];
  if (cursor === 1) return ["Filed Under", bit];
  if (cursor === date) return ["Date", bit];
  if (cursor > date) return ["Language", capitalize(bit)];
  return null;
};

const readGlance = (bits: string[]) => {
  const pairs = new Map<string, string>();
  const about: string[] = [];
  let cursor = 0;
  bits
    .map((raw) => raw.trim())
    .filter(Boolean)
    .forEach((bit) => {
      const slot = slotOf(bit, cursor);
      const at = slot ? SLOTS.indexOf(slot[0]) : -1;
      if (!slot || pairs.has(slot[0]) || at < cursor) {
        about.push(bit);
        return;
      }
      pairs.set(slot[0], slot[1]);
      cursor = at + 1;
    });
  return {
    glance: SLOTS.filter((slot) => pairs.has(slot)).map((label) => ({
      label,
      value: pairs.get(label)!
    })),
    about
  };
};

const lookAtPages = (body: string) => {
  const parts = body
    .replace(/\.$/, "")
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean);
  if (!parts.length) return body ? [{ page: null, text: body }] : [];
  return parts.map((part) => {
    const match = /^p(\d+)\s*(.*)$/.exec(part);
    if (!match) return { page: null, text: part };
    return { page: `p. ${match[1]}`, text: capitalize(match[2].trim()) };
  });
};

const readRange = (line: string): string | null => {
  const match = /^Read:\s*pages\s+(.+)$/.exec(line);
  if (!match) return null;
  const range = match[1]
    .replace(/;\s*grep the rest of the text\.?$/, "")
    .replace(/\.$/, "")
    .trim();
  return range ? `Pages ${range}` : null;
};

const sectionTitle = (
  line: string
): { title: string; note: string | null } | null => {
  if (
    !line.endsWith(":") ||
    line.startsWith("Summary:") ||
    line.startsWith("- ")
  )
    return null;
  const raw = line.slice(0, -1).trim();
  const match = /^(.*?)\s+\((.+)\)$/.exec(raw);
  if (!match) return { title: raw, note: null };
  const note = match[2].trim();
  return { title: match[1].trim(), note: note ? capitalize(note) : null };
};

const sameSupports = (a: string[], b: string[]) =>
  a.length === b.length && a.every((tag, i) => tag === b[i]);

export const presentFileCard = (markdown: string): FileCardSummary => {
  const out: FileCardSummary = {
    title: null,
    glance: [],
    about: [],
    pageCount: null,
    summary: null,
    corrections: [],
    concerns: null,
    fieldGroups: [],
    fieldsNote: null,
    quotes: null,
    sections: [],
    lookAt: [],
    pagesRead: null,
    notes: [],
    footnotes: []
  };
  let current: string | null = null;
  let pastSummary = false;

  const addField = (item: FileCardRow) => {
    let group = out.fieldGroups.find((g) =>
      sameSupports(g.supports, item.supports)
    );
    if (!group) {
      group = { supports: item.supports, rows: [] };
      out.fieldGroups.push(group);
    }
    group.rows.push(item);
  };
  const concerns = (note?: string | null) => {
    out.concerns ??= { title: "Concerns", note: null, rows: [] };
    if (note && !out.concerns.note) out.concerns.note = note;
    return out.concerns;
  };
  const quotes = (note?: string | null) => {
    out.quotes ??= { title: "Quotes", note: null, rows: [] };
    if (note && !out.quotes.note) out.quotes.note = note;
    return out.quotes;
  };
  const other = (title: string, note?: string | null) => {
    let section = out.sections.find((item) => item.title === title);
    if (!section) {
      section = { title, note: note ?? null, rows: [] };
      out.sections.push(section);
    } else if (note && !section.note) section.note = note;
    return section;
  };

  const readLine = (line: string) => {
    if (line.startsWith("## ")) {
      out.title = line.slice(3).trim();
      return;
    }

    const correction = CORRECTION.exec(line);
    if (correction) {
      out.corrections.push({
        by: correction[1]?.trim() || null,
        text: correction[2].trim()
      });
      return;
    }

    const meta = META.exec(line);
    if (meta) {
      const [, id, path, tail] = meta;
      const pages = tail?.match(/^(\d+) pages?$/);
      if (pages) out.pageCount = Number(pages[1]);
      else if (tail) out.notes.push(plain(tail) || tail);
      if (id) out.footnotes.push(`File reference ${id}`);
      if (path) out.footnotes.push(path);
      return;
    }

    if (line.startsWith("Summary:")) {
      out.summary = line.slice("Summary:".length).trim();
      pastSummary = true;
      return;
    }

    if (line.startsWith("Look at the page itself:")) {
      const body = line.slice("Look at the page itself:".length).trim();
      const looked = lookAtPages(body);
      out.lookAt.push(
        ...(looked.length ? looked : [{ page: null, text: line }])
      );
      return;
    }

    if (line.startsWith("Read:")) {
      const range = readRange(line);
      if (range && !out.pagesRead) out.pagesRead = range;
      else out.notes.push(plain(line) || line);
      return;
    }

    const header = sectionTitle(line);
    if (header) {
      current = header.title;
      if (header.title === "Fields") {
        if (header.note) out.fieldsNote ??= header.note;
      } else if (header.title === "Concerns") concerns(header.note);
      else if (header.title === "Quotes") quotes(header.note);
      else other(header.title, header.note);
      return;
    }

    if (line.startsWith("- ")) {
      const field = FIELD.exec(line);
      const quote = field ? null : QUOTE.exec(line);
      const concern = field || quote ? null : CONCERN.exec(line);
      if (field) {
        addField(fieldRow(field[3], field[2], field[4]));
      } else if (quote) {
        const { text, supports } = splitSupports(quote[4]);
        quotes().rows.push(
          row(text, {
            page: pageOf(quote[2]),
            supports,
            key: Boolean(quote[3])
          })
        );
      } else if (concern) {
        const { text, supports } = splitSupports(concern[3]);
        concerns().rows.push(
          row(readable(text), { page: pageOf(concern[2]), supports })
        );
      } else {
        const text = plain(line) || line;
        if (current === "Fields") addField(row(text));
        else if (current === "Concerns") concerns().rows.push(row(text));
        else if (current === "Quotes") quotes().rows.push(row(text));
        else if (current) other(current).rows.push(row(text));
        else out.notes.push(text);
      }
      return;
    }

    const shown = plain(line) || line;
    if (!pastSummary && !current) {
      const { glance, about } = readGlance(shown.split(" · "));
      out.glance.push(...glance);
      out.about.push(...about);
    } else out.notes.push(shown);
  };

  markdown
    .split(/\r?\n/)
    .map((raw) => raw.trim())
    .filter(Boolean)
    .forEach(readLine);

  return out;
};
